import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import baseapi from '../../services/baseapi';
import type { ReportPage, ReportGlobalSettings, WidgetItem, WidgetType } from './types';
import ReportCanvas from './ReportCanvas';

const DEFAULT_GLOBAL_SETTINGS: ReportGlobalSettings = {
  reportTitle: '',
  reportSubtitle: '',
  authorName: '',
  pageFormat: 'A4',
  orientation: 'portrait',
  pageMargin: 24,
  enableHeader: true,
  headerProperties: {
    content: '',
    headerSubtitle: '',
    textAlign: 'left',
    image_url: '',
    logoAlign: 'left',
    logoHeight: 28,
    borderWidth: 1,
    dividerStyle: 'solid',
    borderColor: '#e2e8f0',
    fontSize: 13,
    fontWeight: 'bold',
    color: '#0f172a',
    skipOnFirstPage: false
  },
  enableFooter: true,
  footerProperties: {
    content: '',
    textAlign: 'left',
    showPagination: true,
    paginationFormat: 'page_of_total',
    paginationAlign: 'right',
    footerDateFormat: 'short',
    dateAlign: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    dividerStyle: 'solid',
    fontSize: 12,
    fontWeight: 'normal',
    color: '#64748b'
  },
  enableWatermark: false,
  watermarkSettings: {
    enabled: false,
    type: 'text',
    text: '',
    fontSize: 54,
    color: '#94a3b8',
    opacity: 0.12,
    rotation: -45,
    skipOnFirstPage: false
  }
};

const safeJsonParse = (val: any, fallback: any = {}) => {
  if (!val) return fallback;
  if (typeof val === 'object') return val;
  if (typeof val === 'string') {
    try {
      return JSON.parse(val);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const isTableWidget = (w: WidgetItem) =>
  w.type === 'table' ||
  (w.type === 'chart' &&
    (w.properties?.chartType === 'table' ||
      w.properties?.chart_type === 'table' ||
      w.properties?.widget_type === 'table'));

const fetchTableData = async (tableWidget: WidgetItem, levelObj: any, reqConfig: any = {}) => {
  let qDef = safeJsonParse(tableWidget.properties?.query_definition, null);
  if (!qDef && (tableWidget.properties?.dataSource || tableWidget.properties?.dataset_name)) {
    const src = tableWidget.properties.dataSource || tableWidget.properties.dataset_name;
    const metricType = tableWidget.properties.aggregation || tableWidget.properties.yAxisAggregation || 'count';
    const metricField = tableWidget.properties.metric_field || tableWidget.properties.yAxisField || null;
    const xAxisField = tableWidget.properties.xAxisField || tableWidget.properties.group_by_field || null;
    qDef = {
      source: src,
      metrics: [{ type: metricType, field: metricField, alias: metricField || 'metric' }],
      x_axis: xAxisField ? { field: xAxisField } : null,
      filters: tableWidget.properties.filters || [],
      columns: tableWidget.properties.columns || [],
      limit: 100
    };
  }

  if (!qDef?.source) return { rows: [], columns: [] };

  try {
    const params: any = {};
    const payloadQDef = JSON.parse(JSON.stringify(qDef));
    // Strictly pass user configured limit directly from query_definition or properties
    const configuredLimit = payloadQDef.limit !== undefined
      ? Number(payloadQDef.limit)
      : (tableWidget.properties?.limit !== undefined ? Number(tableWidget.properties.limit) : undefined);
    if (configuredLimit !== undefined) {
      payloadQDef.limit = configuredLimit;
    }

    if (levelObj && levelObj.name !== 'global' && levelObj.id) {
      params.client_id = levelObj.id;
      payloadQDef.client_id = levelObj.id;
    }

    const res = await baseapi.post('/cdash/query', payloadQDef, { ...reqConfig, params });
    const queryData = res.data;
    const rows: any[] = Array.isArray(queryData?.rows)
      ? queryData.rows
      : (Array.isArray(queryData?.data?.rows)
        ? queryData.data.rows
        : []);
    const columns: string[] = Array.isArray(queryData?.columns)
      ? queryData.columns
      : (Array.isArray(queryData?.data?.columns)
        ? queryData.data.columns
        : []);
    return { rows, columns };
  } catch (err) {
    console.warn('Failed to query table data:', err);
    return { rows: [], columns: [] };
  }
};

const expandReportPagesWithTables = async (
  inputPages: ReportPage[],
  levelObj: any,
  reqConfig: any = {},
  globalSettings?: ReportGlobalSettings | null
): Promise<ReportPage[]> => {
  const expanded: ReportPage[] = [];
  const isLandscape = globalSettings?.orientation === 'landscape';
  const MAX_PAGE_GRID_HEIGHT = isLandscape ? 16 : 24;
  const FULL_PAGE_CAP = isLandscape ? 18 : 28;

  for (let pIdx = 0; pIdx < inputPages.length; pIdx++) {
    const page = inputPages[pIdx];
    const widgets = page.widgets || [];

    // Separate header/footer vs body widgets
    const bodyWidgets = widgets.filter(w => w.type !== 'header' && w.type !== 'footer');
    const tableCount = bodyWidgets.filter(isTableWidget).length;

    if (tableCount === 0) {
      expanded.push(page);
      continue;
    }

    // Sort body widgets by Y (and X)
    const sortedWidgets = [...bodyWidgets].sort(
      (a, b) => (Number(a.y) || 0) - (Number(b.y) || 0) || (Number(a.x) || 0) - (Number(b.x) || 0)
    );

    // Group sorted widgets into horizontal bands
    const bands: Array<{ isTable: boolean; widgets: WidgetItem[] }> = [];
    let currentBand: WidgetItem[] = [];
    let currentIsTable = false;

    sortedWidgets.forEach((w) => {
      const isTab = isTableWidget(w);
      if (isTab) {
        if (currentBand.length > 0) {
          bands.push({ isTable: currentIsTable, widgets: currentBand });
          currentBand = [];
        }
        bands.push({ isTable: true, widgets: [w] });
        currentIsTable = false;
      } else {
        if (currentIsTable && currentBand.length > 0) {
          bands.push({ isTable: true, widgets: currentBand });
          currentBand = [];
        }
        currentIsTable = false;
        currentBand.push(w);
      }
    });
    if (currentBand.length > 0) {
      bands.push({ isTable: currentIsTable, widgets: currentBand });
    }

    // Page accumulator
    let curPageWidgets: WidgetItem[] = [];
    let curUsedHeight = 0;
    let pageSeq = 1;

    const flushPage = () => {
      if (curPageWidgets.length > 0) {
        expanded.push({
          id: `${page.id}-p${pageSeq++}`,
          widgets: curPageWidgets
        });
        curPageWidgets = [];
        curUsedHeight = 0;
      }
    };

    for (const band of bands) {
      if (!band.isTable) {
        // Non-table widgets (charts, text, metrics)
        const minY = band.widgets.reduce((min, w) => Math.min(min, Number(w.y) || 0), Infinity);
        const maxY = band.widgets.reduce((max, w) => Math.max(max, (Number(w.y) || 0) + (Number(w.h) || 4)), 0);
        const bHeight = maxY - (minY === Infinity ? 0 : minY);

        if (curUsedHeight + bHeight > 24 && curUsedHeight > 0) {
          flushPage();
        }

        const shifted = band.widgets.map((w) => ({
          ...w,
          y: curUsedHeight + ((Number(w.y) || 0) - (minY === Infinity ? 0 : minY))
        }));
        curPageWidgets.push(...shifted);
        curUsedHeight += bHeight;
      } else {
        // Table widget
        const tblWidget = band.widgets[0];
        const { rows, columns } = await fetchTableData(tblWidget, levelObj, reqConfig);

        let availableHeight = MAX_PAGE_GRID_HEIGHT - curUsedHeight;
        if (availableHeight < 6 && curUsedHeight > 0) {
          flushPage();
          availableHeight = MAX_PAGE_GRID_HEIGHT;
        }

        const isAloneOnPage = curUsedHeight === 0;
        const initialCap = (isAloneOnPage || availableHeight >= (MAX_PAGE_GRID_HEIGHT - 2))
          ? FULL_PAGE_CAP
          : Math.max(3, Math.floor(((availableHeight * 32) - 45) / 28));

        if (rows.length <= initialCap) {
          // Fits entirely on current page
          const tHeight = isAloneOnPage ? MAX_PAGE_GRID_HEIGHT : Math.min(availableHeight, Math.max(4, Math.ceil((rows.length * 28 + 45) / 32)));
          curPageWidgets.push({
            ...tblWidget,
            y: curUsedHeight,
            h: tHeight,
            properties: {
              ...tblWidget.properties,
              preloadedRows: rows,
              preloadedColumns: columns
            }
          });
          curUsedHeight += tHeight;
        } else {
          // Table overflows onto continuation pages
          const firstSlice = rows.slice(0, initialCap);
          const remainingRows = rows.slice(initialCap);

          curPageWidgets.push({
            ...tblWidget,
            y: curUsedHeight,
            h: availableHeight,
            properties: {
              ...tblWidget.properties,
              preloadedRows: firstSlice,
              preloadedColumns: columns
            }
          });
          flushPage();

          const totalContPages = Math.ceil(remainingRows.length / FULL_PAGE_CAP);

          for (let cIdx = 0; cIdx < totalContPages; cIdx++) {
            const chunk = remainingRows.slice(cIdx * FULL_PAGE_CAP, (cIdx + 1) * FULL_PAGE_CAP);
            const isLast = cIdx === totalContPages - 1;

            if (!isLast) {
              curPageWidgets.push({
                id: `${tblWidget.id}-cont-${pageSeq}`,
                type: tblWidget.type,
                x: 0,
                y: 0,
                w: 12,
                h: MAX_PAGE_GRID_HEIGHT,
                properties: {
                  ...tblWidget.properties,
                  title: tblWidget.properties.title || tblWidget.properties.name
                    ? `${tblWidget.properties.title || tblWidget.properties.name} (Cont.)`
                    : '',
                  preloadedRows: chunk,
                  preloadedColumns: columns
                }
              });
              flushPage();
            } else {
              // Last chunk
              const lastHeight = Math.min(MAX_PAGE_GRID_HEIGHT, Math.max(4, Math.ceil((chunk.length * 28 + 45) / 32)));
              curPageWidgets.push({
                id: `${tblWidget.id}-cont-${pageSeq}`,
                type: tblWidget.type,
                x: 0,
                y: 0,
                w: 12,
                h: lastHeight,
                properties: {
                  ...tblWidget.properties,
                  title: tblWidget.properties.title || tblWidget.properties.name
                    ? `${tblWidget.properties.title || tblWidget.properties.name} (Cont.)`
                    : '',
                  preloadedRows: chunk,
                  preloadedColumns: columns
                }
              });
              curUsedHeight = lastHeight;
            }
          }
        }
      }
    }

    flushPage();
  }

  return expanded;
};

export const ReportPrintViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pages, setPages] = useState<ReportPage[]>([]);
  const [globalSettings, setGlobalSettings] = useState<ReportGlobalSettings>(DEFAULT_GLOBAL_SETTINGS);

  useEffect(() => {
    const fetchReport = async () => {
      if (!id) {
        setError('No report ID specified');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const queryToken = searchParams.get('token') || searchParams.get('access_token') || searchParams.get('auth_token');
        const reqConfig = queryToken ? { headers: { Authorization: `Bearer ${queryToken}` } } : {};
        const res = await baseapi.get(`custom_reports/${id}`, reqConfig);
        const raw = res.data;
        const reportMetaObj = raw?.data || raw;

        if (!reportMetaObj) {
          setError('Report not found');
          setLoading(false);
          return;
        }

        // Ext
        // ract Global Settings
        const rawGlobalSettings = raw?.global_settings || raw?.globalSettings || reportMetaObj?.global_settings || reportMetaObj?.globalSettings || {};
        const extraSettings = safeJsonParse(rawGlobalSettings.extra_settings || rawGlobalSettings.extraSettings, {});
        const rawHeaderProps = safeJsonParse(rawGlobalSettings.header_properties || rawGlobalSettings.headerProperties, {});
        const rawFooterProps = safeJsonParse(rawGlobalSettings.footer_properties || rawGlobalSettings.footerProperties, {});

        const loadedSettings: ReportGlobalSettings = {
          reportTitle: rawGlobalSettings.report_name || rawGlobalSettings.reportTitle || reportMetaObj.report_name || reportMetaObj.name || '',
          reportSubtitle: '',
          authorName: rawGlobalSettings.authorName || rawGlobalSettings.author_name || extraSettings.authorName || reportMetaObj.author || '',
          pageFormat: rawGlobalSettings.page_format || rawGlobalSettings.pageFormat || 'A4',
          pageMargin: rawGlobalSettings.page_margin !== undefined ? Number(rawGlobalSettings.page_margin) : 24,
          enableHeader: rawGlobalSettings.enable_header !== undefined ? Boolean(rawGlobalSettings.enable_header) : true,
          headerProperties: {
            content: rawHeaderProps.content !== undefined ? rawHeaderProps.content : '',
            headerSubtitle: rawHeaderProps.headerSubtitle !== undefined ? rawHeaderProps.headerSubtitle : (rawHeaderProps.header_subtitle !== undefined ? rawHeaderProps.header_subtitle : ''),
            textAlign: rawHeaderProps.textAlign || rawHeaderProps.text_align || 'left',
            image_url: rawHeaderProps.image_url || rawHeaderProps.logoUrl || rawHeaderProps.logo_url || (rawHeaderProps.image_key ? `/api/${rawHeaderProps.image_key}` : ''),
            image_key: rawHeaderProps.image_key || rawHeaderProps.imageKey || '',
            image_name: rawHeaderProps.image_name || rawHeaderProps.imageName || '',
            original_name: rawHeaderProps.original_name || rawHeaderProps.originalName || '',
            size: rawHeaderProps.size,
            content_type: rawHeaderProps.content_type || rawHeaderProps.contentType,
            logoAlign: rawHeaderProps.logoAlign || rawHeaderProps.logo_align || 'left',
            logoHeight: Number(rawHeaderProps.logoHeight || rawHeaderProps.logo_height || 28),
            borderWidth: rawHeaderProps.borderWidth !== undefined ? Number(rawHeaderProps.borderWidth) : (rawHeaderProps.border_width !== undefined ? Number(rawHeaderProps.border_width) : 1),
            dividerStyle: rawHeaderProps.dividerStyle || rawHeaderProps.divider_style || 'solid',
            borderColor: rawHeaderProps.borderColor || rawHeaderProps.border_color || '#e2e8f0',
            fontSize: Number(rawHeaderProps.fontSize || 13),
            fontWeight: rawHeaderProps.fontWeight || 'bold',
            color: rawHeaderProps.color || '#0f172a',
            skipOnFirstPage: rawHeaderProps.skipOnFirstPage !== undefined ? Boolean(rawHeaderProps.skipOnFirstPage) : (rawHeaderProps.skip_on_first_page !== undefined ? Boolean(rawHeaderProps.skip_on_first_page) : false)
          },
          enableFooter: rawGlobalSettings.enable_footer !== undefined ? Boolean(rawGlobalSettings.enable_footer) : true,
          footerProperties: {
            ...DEFAULT_GLOBAL_SETTINGS.footerProperties,
            ...rawFooterProps,
            textAlign: rawFooterProps.textAlign || rawFooterProps.text_align || 'left',
            paginationAlign: rawFooterProps.paginationAlign || rawFooterProps.pagination_align || 'right'
          },
          enableWatermark: rawGlobalSettings.enable_watermark !== undefined
            ? Boolean(rawGlobalSettings.enable_watermark)
            : (extraSettings.enable_watermark !== undefined ? Boolean(extraSettings.enable_watermark) : false),
          watermarkSettings: {
            enabled: rawGlobalSettings.enable_watermark !== undefined
              ? Boolean(rawGlobalSettings.enable_watermark)
              : (extraSettings.enable_watermark !== undefined ? Boolean(extraSettings.enable_watermark) : false),
            type: rawGlobalSettings.watermark_settings?.type || extraSettings.watermark_settings?.type || 'text',
            text: rawGlobalSettings.watermark_settings?.text !== undefined ? rawGlobalSettings.watermark_settings.text : (extraSettings.watermark_settings?.text !== undefined ? extraSettings.watermark_settings.text : ''),
            image_url: rawGlobalSettings.watermark_settings?.image_url || extraSettings.watermark_settings?.image_url || '',
            imageWidth: Number(rawGlobalSettings.watermark_settings?.imageWidth || extraSettings.watermark_settings?.imageWidth || 350),
            fontSize: Number(rawGlobalSettings.watermark_settings?.fontSize || extraSettings.watermark_settings?.fontSize || 54),
            color: rawGlobalSettings.watermark_settings?.color || extraSettings.watermark_settings?.color || '#94a3b8',
            opacity: rawGlobalSettings.watermark_settings?.opacity !== undefined ? Number(rawGlobalSettings.watermark_settings.opacity) : (extraSettings.watermark_settings?.opacity !== undefined ? Number(extraSettings.watermark_settings.opacity) : 0.12),
            rotation: rawGlobalSettings.watermark_settings?.rotation !== undefined ? Number(rawGlobalSettings.watermark_settings.rotation) : (extraSettings.watermark_settings?.rotation !== undefined ? Number(extraSettings.watermark_settings.rotation) : -45),
            skipOnFirstPage: rawGlobalSettings.watermark_settings?.skipOnFirstPage !== undefined
              ? Boolean(rawGlobalSettings.watermark_settings.skipOnFirstPage)
              : (rawGlobalSettings.watermark_settings?.skip_on_first_page !== undefined
                ? Boolean(rawGlobalSettings.watermark_settings.skip_on_first_page)
                : (extraSettings.watermark_settings?.skipOnFirstPage !== undefined
                  ? Boolean(extraSettings.watermark_settings.skipOnFirstPage)
                  : (extraSettings.watermark_settings?.skip_on_first_page !== undefined
                    ? Boolean(extraSettings.watermark_settings.skip_on_first_page)
                    : (extraSettings.watermarkSettings?.skipOnFirstPage !== undefined
                      ? Boolean(extraSettings.watermarkSettings.skipOnFirstPage)
                      : false))))
          }
        };
        setGlobalSettings(loadedSettings);

        // Extract Widgets & Build Pages
        const rawWidgets = Array.isArray(raw?.widgets)
          ? raw.widgets
          : (Array.isArray(reportMetaObj?.widgets) ? reportMetaObj.widgets : []);

        let safePages: ReportPage[] = [];
        if (rawWidgets.length > 0) {
          const pageMap = new Map<number, WidgetItem[]>();
          rawWidgets.forEach((w: any, wIdx: number) => {
            if (w.widget_type === 'header' || w.widget_type === 'footer' || w.type === 'header' || w.type === 'footer') {
              return;
            }
            const pageNum = Number(w.page_number) || 1;
            const pList = pageMap.get(pageNum) || [];
            const parsedProperties = safeJsonParse(w.properties, {});
            const mergedProperties = {
              ...parsedProperties,
              ...(w.image_url ? { image_url: w.image_url } : {}),
              ...(w.image_key ? { image_key: w.image_key } : {}),
              ...(w.image_name ? { image_name: w.image_name } : {}),
              ...(w.original_name ? { original_name: w.original_name } : {})
            };
            pList.push({
              id: String(w.id !== undefined ? w.id : `w-${wIdx}`),
              type: (w.widget_type || w.type || 'text') as WidgetType,
              x: Number.isFinite(Number(w.pos_x)) ? Number(w.pos_x) : (Number.isFinite(Number(w.x)) ? Number(w.x) : 0),
              y: Number.isFinite(Number(w.pos_y)) ? Number(w.pos_y) : (Number.isFinite(Number(w.y)) ? Number(w.y) : 0),
              w: Number.isFinite(Number(w.width)) ? Number(w.width) : (Number.isFinite(Number(w.w)) ? Number(w.w) : 6),
              h: Number.isFinite(Number(w.height)) ? Number(w.height) : (Number.isFinite(Number(w.h)) ? Number(w.h) : 4),
              properties: mergedProperties
            });
            pageMap.set(pageNum, pList);
          });

          const maxPageNum = Math.max(1, ...Array.from(pageMap.keys()));
          safePages = Array.from({ length: maxPageNum }, (_, idx) => {
            const pNum = idx + 1;
            return {
              id: `page-${pNum}`,
              widgets: pageMap.get(pNum) || []
            };
          });
        } else if (Array.isArray(reportMetaObj.pages) && reportMetaObj.pages.length > 0) {
          safePages = reportMetaObj.pages.filter(Boolean).map((p: any, pIdx: number) => ({
            id: String(p?.id || p?._id || `page-${pIdx + 1}`),
            widgets: Array.isArray(p?.widgets) ? p.widgets.filter((w: any) => w && w.type !== 'header' && w.type !== 'footer' && w.widget_type !== 'header' && w.widget_type !== 'footer').map((w: any, wIdx: number) => {
              const parsedProps = safeJsonParse(w?.properties, {});
              const mergedProps = {
                ...parsedProps,
                ...(w?.image_url ? { image_url: w.image_url } : {}),
                ...(w?.image_key ? { image_key: w.image_key } : {}),
                ...(w?.image_name ? { image_name: w.image_name } : {}),
                ...(w?.original_name ? { original_name: w.original_name } : {})
              };
              return {
                id: String(w?.id || w?._id || `w-${wIdx}`),
                type: (w?.widget_type || w?.type || 'text') as WidgetType,
                x: Number.isFinite(Number(w?.pos_x)) ? Number(w.pos_x) : (Number.isFinite(Number(w?.x)) ? Number(w.x) : 0),
                y: Number.isFinite(Number(w?.pos_y)) ? Number(w.pos_y) : (Number.isFinite(Number(w?.y)) ? Number(w.y) : 0),
                w: Number.isFinite(Number(w?.width)) ? Number(w.width) : (Number.isFinite(Number(w?.w)) ? Number(w.w) : 6),
                h: Number.isFinite(Number(w?.height)) ? Number(w.height) : (Number.isFinite(Number(w?.h)) ? Number(w.h) : 4),
                properties: mergedProps
              };
            }) : []
          }));
        }

        // Expand pages dynamically for large tables
        const storedLevel = localStorage.getItem('level');
        const levelObj = storedLevel ? safeJsonParse(storedLevel, null) : null;
        const expandedPages = await expandReportPagesWithTables(safePages, levelObj, reqConfig, loadedSettings);

        setPages(expandedPages);
        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load report');
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: '#ffffff' }}>
        <CircularProgress size={32} sx={{ color: '#0f172a' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#ffffff', minHeight: '100vh' }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'visible',
        '@media print': {
          p: 0,
          m: 0,
          bgcolor: '#ffffff'
        }
      }}
    >
      <style>{`
        @page {
          size: ${globalSettings?.pageFormat === 'Letter' ? 'letter' : 'A4'} ${globalSettings?.orientation === 'landscape' ? 'landscape' : 'portrait'};
          margin: 0;
        }
        *, *::before, *::after {
          -webkit-animation: none !important;
          animation: none !important;
          -webkit-transition: none !important;
          transition: none !important;
        }
        .apexcharts-canvas, .apexcharts-canvas *, .apexcharts-svg, .apexcharts-svg * {
          transition: none !important;
          animation: none !important;
        }
        @media print {
          table {
            table-layout: fixed !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          th, td {
            word-break: break-word !important;
            overflow-wrap: anywhere !important;
            white-space: normal !important;
          }
          div[style*="overflow-x: auto"], div[style*="overflow: auto"] {
            overflow-x: visible !important;
          }
        }
      `}</style>
      <ReportCanvas
        pages={pages}
        activePageId={pages[0]?.id || 'page-1'}
        selectedId={null}
        globalSettings={globalSettings}
        readOnly={true}
        onSelectWidget={() => { }}
        onLayoutChange={() => { }}
        onAddPage={() => { }}
      />
    </Box>
  );
};

export default ReportPrintViewer;
